import { GoogleGenerativeAI, SchemaType } from '@google/generative-ai';
import { TOKEN_TARJETA } from './config';

const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const MODEL = process.env.REACT_APP_GEMINI_MODEL || 'gemini-2.5-flash';

const genAI = API_KEY ? new GoogleGenerativeAI(API_KEY) : null;

// Esquema de salida estructurada: obligamos a Gemini a decirnos si pudo
// responder con el contexto o no, en vez de inventar.
const responseSchema = {
  type: SchemaType.OBJECT,
  properties: {
    answerable: {
      type: SchemaType.BOOLEAN,
      description:
        'true solo si la pregunta puede responderse con la información del CONTEXTO. false si el contexto no contiene la información necesaria.',
    },
    answer: {
      type: SchemaType.STRING,
      description:
        'Si answerable es true, la respuesta para el usuario. Si es false, deja una cadena vacía.',
    },
  },
  required: ['answerable', 'answer'],
};

const buildSystemInstruction = (context) => `
Eres el asistente de atención al cliente de "Rocharamas", que vende RETRATOS.
Respondes de forma clara, amable y breve.

REGLA CRÍTICA: solo puedes responder usando la información del CONTEXTO de abajo,
las POLÍTICAS FIJAS de esta instrucción y lo que el propio usuario haya dicho en
la conversación. No inventes datos, precios, horarios, políticas ni nada que no
esté en el CONTEXTO ni en las POLÍTICAS FIJAS.

- Si la pregunta SÍ se puede responder con el CONTEXTO o las POLÍTICAS FIJAS: pon
  answerable=true y escribe la respuesta en "answer".
- Si la información NO está disponible (no la sabes con certeza): pon
  answerable=false y deja "answer" vacío. NO intentes adivinar.
- Para saludos y charla trivial (hola, gracias, etc.) puedes responder con
  cortesía: answerable=true.

IDIOMA:
- RESPONDE en el mismo idioma en que te escribe el cliente (español, inglés o
  francés). Si no es claro, responde en español.

MONEDA:
- Los precios OFICIALES están en pesos mexicanos (MXN) y así deben mencionarse
  siempre como referencia oficial.
- Si el cliente parece ser de Estados Unidos o Canadá (escribe en inglés/francés
  o lo menciona), además del precio en MXN conviértelo de forma aproximada a su
  moneda (USD o CAD) y ACLARA que es una conversión aproximada al tipo de cambio
  actual y que el precio oficial es en MXN. Aplica lo mismo a los envíos.

=== POLÍTICAS FIJAS (siempre vigentes) ===

ENVÍOS:
- Por ahora SOLO hacemos envíos a México, Estados Unidos y Canadá. Si el cliente
  es de otro país, indícale con amabilidad que por el momento no tenemos envíos a
  su país.

FLUJO DE COMPRA DE UN RETRATO:
1. El cliente puede enviarnos la FOTO que quiere convertir en retrato. Es normal
   recibir imágenes; agradécelas.
2. Para entregarle la VISTA PREVIA del retrato ya procesado es OBLIGATORIO que
   primero realice el PAGO. No prometas ni envíes la vista previa antes del pago.
3. Cuando el cliente quiera pagar o pida los datos de pago, dale instrucciones
   breves e incluye EXACTAMENTE el token ${TOKEN_TARJETA} en tu respuesta (en su
   propia línea). NUNCA escribas tú el número de tarjeta; el sistema mostrará la
   tarjeta con ese token. En el concepto el cliente debe poner su nombre.
4. Pídele que, una vez pagado, nos envíe su COMPROBANTE DE PAGO por este chat
   (puede subir la imagen con el botón de adjuntar) para verificarlo.
5. Después del pago confirmado, NUESTRO EQUIPO lo contactará por este mismo chat
   para acordar la imagen.

=== FIN POLÍTICAS FIJAS ===

=== CONTEXTO ===
${context && context.trim() ? context : '(El administrador aún no ha configurado información de contexto.)'}
=== FIN DEL CONTEXTO ===
`.trim();

/**
 * Pregunta a Gemini.
 * @param {Object} p
 * @param {string} p.context  Contexto configurado por el administrador.
 * @param {Array<{role:'user'|'assistant', text:string}>} p.history  Historial previo.
 * @param {string} p.question  Pregunta nueva del usuario.
 * @param {string} [p.phone]   Teléfono del usuario (E.164) para deducir idioma y moneda.
 * @returns {Promise<{answerable:boolean, answer:string}>}
 */
export async function askGemini({ context, history = [], question }) {
  if (!genAI) {
    throw new Error(
      'Falta REACT_APP_GEMINI_API_KEY. Copia .env.example a .env y agrega tu API key de Gemini.'
    );
  }

  const model = genAI.getGenerativeModel({
    model: MODEL,
    systemInstruction: buildSystemInstruction(context),
    generationConfig: {
      responseMimeType: 'application/json',
      responseSchema,
      temperature: 0.4,
    },
  });

  // Convertir historial al formato de Gemini (assistant -> model).
  const contents = history.map((m) => ({
    role: m.role === 'assistant' ? 'model' : 'user',
    parts: [{ text: m.text }],
  }));
  contents.push({ role: 'user', parts: [{ text: question }] });

  const result = await model.generateContent({ contents });
  const raw = result.response.text();

  try {
    const parsed = JSON.parse(raw);
    return {
      answerable: !!parsed.answerable,
      answer: typeof parsed.answer === 'string' ? parsed.answer : '',
    };
  } catch (e) {
    // Si por algún motivo no devolvió JSON válido, lo tratamos como no respondible.
    return { answerable: false, answer: '' };
  }
}

/**
 * Genera un resumen breve de una conversación para el administrador.
 * @param {Array<{role:'user'|'assistant', text:string}>} mensajes
 * @returns {Promise<string>}
 */
export async function resumirConversacion(mensajes = []) {
  if (!genAI) {
    throw new Error(
      'Falta REACT_APP_GEMINI_API_KEY. Copia .env.example a .env y agrega tu API key de Gemini.'
    );
  }
  if (!mensajes.length) return 'La conversación está vacía.';

  const model = genAI.getGenerativeModel({
    model: MODEL,
    generationConfig: { temperature: 0.3 },
  });

  const transcripcion = mensajes
    .map((m) => `${m.role === 'user' ? 'Cliente' : 'Asistente'}: ${m.text}`)
    .join('\n');

  const prompt = `Eres asistente del administrador de "Rocharamas". Resume en español, de forma clara y breve, la siguiente conversación de atención al cliente. Usa este formato:

- **Resumen:** (2-3 líneas de qué trató la conversación)
- **Lo que pidió/preguntó el cliente:** (viñetas)
- **Qué se le respondió / estado:** (viñetas)
- **Pendientes o acciones sugeridas:** (viñetas; si no hay, escribe "Ninguno")

No inventes datos que no estén en la conversación.

=== CONVERSACIÓN ===
${transcripcion}
=== FIN ===`;

  const result = await model.generateContent(prompt);
  return result.response.text().trim();
}
